// feature/fallos-grouped-view.
// Per Molly 2026-05-07: "Necesito ayuda para poder agruparlos … Los
// fallos favorables de 1 y 2 instancia, yo no los reviso, no hago
// ninguna gestión y me salen para gestionar."
//
// Renders fallos grouped by tipo_fallo with collapsible sections.
// Order = most-actionable first. Default expand state mirrors that:
// the buckets she works on open by default; the buckets she doesn't
// (favorables, no_pedir_insumo) open only on explicit click.
//
// User-set state persists in localStorage per user-id, so once Molly
// adjusts the defaults she sees her layout on every visit.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TipoFalloPill, EstadoPill } from "../Pills";

const fmt = (d) => d ? new Date(d).toLocaleDateString("es-CO", { day: "2-digit", month: "short" }) : "—";

const GROUP_ORDER = [
  { key: "DESFAVORABLE_1A",  label: "Desfavorables 1ª instancia", defaultExpanded: true,
    matches: (tf) => tf === "DESFAVORABLE_1A" },
  { key: "DESFAVORABLE_2A",  label: "Desfavorables 2ª instancia", defaultExpanded: true,
    matches: (tf) => tf === "DESFAVORABLE_2A" },
  { key: "NULIDAD",          label: "Nulidad",                    defaultExpanded: true,
    matches: (tf) => tf === "NULIDAD" },
  { key: "SIN_CLASIFICAR",   label: "Sin clasificar",             defaultExpanded: true,
    matches: (tf) => !tf },
  { key: "NO_PEDIR_INSUMO",  label: "No pedir insumo",            defaultExpanded: false,
    matches: (tf) => tf === "NO_PEDIR_INSUMO" },
  { key: "FAVORABLE_1A",     label: "Favorables 1ª instancia",    defaultExpanded: false,
    matches: (tf) => tf === "FAVORABLE_1A" },
  { key: "FAVORABLE_2A",     label: "Favorables 2ª instancia",    defaultExpanded: false,
    matches: (tf) => tf === "FAVORABLE_2A" },
];

function defaultState() {
  return GROUP_ORDER.reduce((acc, g) => { acc[g.key] = g.defaultExpanded; return acc; }, {});
}

function ChevronRight({ open }) {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
         style={{
           flexShrink: 0,
           color: "var(--text-muted)",
           transform: open ? "rotate(90deg)" : "rotate(0deg)",
           transition: "transform 0.15s ease",
         }}
         aria-hidden="true">
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );
}

export default function GroupedFallosView({ cases, onOpenCase, userId }) {
  const storageKey = userId != null
    ? `eds_fallos_section_state_${userId}`
    : "eds_fallos_section_state";

  const [expanded, setExpanded] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        // Merge persisted state over defaults so newly-introduced groups
        // pick up their defaultExpanded value rather than `undefined`.
        return { ...defaultState(), ...parsed };
      }
    } catch (_) { /* fall through to defaults */ }
    return defaultState();
  });

  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(expanded)); }
    catch (_) { /* ignore quota / private mode errors */ }
  }, [expanded, storageKey]);

  const toggle = useCallback((key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const grouped = useMemo(() => {
    return GROUP_ORDER
      .map((g) => ({ ...g, cases: cases.filter((c) => g.matches(c.tipo_fallo)) }))
      .filter((g) => g.cases.length > 0);
  }, [cases]);

  if (grouped.length === 0) {
    return null;
  }

  return (
    <div className="col" style={{ gap: 10 }}>
      {grouped.map((g) => (
        <FallosGroup key={g.key}
                     group={g}
                     expanded={!!expanded[g.key]}
                     onToggle={() => toggle(g.key)}
                     onOpenCase={onOpenCase} />
      ))}
    </div>
  );
}

function FallosGroup({ group, expanded, onToggle, onOpenCase }) {
  return (
    <section className="card" style={{ padding: 0, overflow: "hidden" }}>
      <button type="button"
              onClick={onToggle}
              aria-expanded={expanded}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                width: "100%",
                padding: "12px 16px",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 14,
                fontWeight: 600,
                color: "var(--text-primary)",
                textAlign: "left",
              }}>
        <ChevronRight open={expanded} />
        <span style={{ flex: 1 }}>{group.label}</span>
        <span style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          minWidth: 24, padding: "1px 10px",
          background: "var(--bg-subtle)",
          color: "var(--text-muted)",
          borderRadius: 999,
          fontSize: 11, fontWeight: 600,
        }}>
          {group.cases.length}
        </span>
      </button>
      {expanded && (
        <div style={{
          borderTop: "1px solid var(--border-hairline)",
        }}>
          <table>
            <thead>
              <tr>
                <th>Caso</th>
                <th>Tipo de fallo</th>
                <th>Accionante</th>
                <th>Radicado</th>
                <th>Juzgado</th>
                <th>Fecha</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {group.cases.map((c) => (
                <tr key={c.id}
                    onClick={() => onOpenCase(c.id)}
                    style={{ cursor: "pointer" }}>
                  <td><strong>{c.case_number}</strong></td>
                  <td><TipoFalloPill value={c.tipo_fallo} missingLabel="Sin clasificar" /></td>
                  <td>{c.accionante_nombre || "—"}</td>
                  <td style={{ fontFamily: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace", fontSize: 11 }}>
                    {c.radicado || "—"}
                  </td>
                  <td>{c.juzgado || "—"}</td>
                  <td>{fmt(c.fecha_ingreso)}</td>
                  <td><EstadoPill c={c} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
